/**
 * Profile Titles - automatic progression based on total earned crystals.
 * Titles are milestones, not purchasable items.
 */

export type TitleId =
  | "titleNovice"
  | "titleBeginner"
  | "titleExplorer"
  | "titlePupil"
  | "titleStudent"
  | "titleGraduate"
  | "titleConnoisseur"
  | "titleLiterate"
  | "titleBookworm"
  | "titleLinguist"
  | "titleTranslator"
  | "titlePro"
  | "titleExpert"
  | "titlePolyglot"
  | "titleErudite"
  | "titleMaster"
  | "titleMagister"
  | "titleProfessor"
  | "titleAcademic"
  | "titleVirtuoso"
  | "titleGenius"
  | "titleGuru"
  | "titleAIMaster"
  | "titleThinker"
  | "titleSage"
  | "titleSeer"
  | "titleLegend"
  | "titleMyth"
  | "titleImmortal"
  | "titleAbsolute";

export interface TitleDef {
  id: TitleId;
  threshold: number;
  lucideIcon:
    | "Baby"
    | "Sprout"
    | "Search"
    | "GraduationCap"
    | "Book"
    | "Languages"
    | "Award"
    | "Star"
    | "Crown"
    | "Ghost"
    | "Zap"
    | "Sparkles"
    | "Brain"
    | "BookOpen"
    | "Eye"
    | "Rocket";
}

export const TITLE_DEFINITIONS: TitleDef[] = [
  { id: "titleNovice", threshold: 0, lucideIcon: "Baby" },
  { id: "titleBeginner", threshold: 200, lucideIcon: "Sprout" },
  { id: "titleExplorer", threshold: 500, lucideIcon: "Search" },
  { id: "titlePupil", threshold: 1000, lucideIcon: "GraduationCap" },
  { id: "titleStudent", threshold: 1500, lucideIcon: "Book" },
  { id: "titleGraduate", threshold: 2000, lucideIcon: "GraduationCap" },
  { id: "titleConnoisseur", threshold: 2500, lucideIcon: "Award" },
  { id: "titleLiterate", threshold: 3000, lucideIcon: "Book" },
  { id: "titleBookworm", threshold: 4000, lucideIcon: "BookOpen" },
  { id: "titleLinguist", threshold: 5000, lucideIcon: "Languages" },
  { id: "titleTranslator", threshold: 6500, lucideIcon: "Languages" },
  { id: "titlePro", threshold: 8000, lucideIcon: "Award" },
  { id: "titleExpert", threshold: 10000, lucideIcon: "Star" },
  { id: "titlePolyglot", threshold: 12000, lucideIcon: "Languages" },
  { id: "titleErudite", threshold: 15000, lucideIcon: "Award" },
  { id: "titleMaster", threshold: 18000, lucideIcon: "Crown" },
  { id: "titleMagister", threshold: 22000, lucideIcon: "GraduationCap" },
  { id: "titleProfessor", threshold: 26000, lucideIcon: "GraduationCap" },
  { id: "titleAcademic", threshold: 30000, lucideIcon: "Award" },
  { id: "titleVirtuoso", threshold: 35000, lucideIcon: "Zap" },
  { id: "titleGenius", threshold: 40000, lucideIcon: "Zap" },
  { id: "titleGuru", threshold: 45000, lucideIcon: "Star" },
  { id: "titleAIMaster", threshold: 50000, lucideIcon: "Sparkles" },
  { id: "titleThinker", threshold: 60000, lucideIcon: "Brain" },
  { id: "titleSage", threshold: 70000, lucideIcon: "BookOpen" },
  { id: "titleSeer", threshold: 85000, lucideIcon: "Eye" },
  { id: "titleLegend", threshold: 100000, lucideIcon: "Crown" },
  { id: "titleMyth", threshold: 150000, lucideIcon: "Ghost" },
  { id: "titleImmortal", threshold: 250000, lucideIcon: "Rocket" },
  { id: "titleAbsolute", threshold: 500000, lucideIcon: "Crown" },
];

export interface TitleProgress {
  current: TitleDef;
  next: TitleDef | null;
  currentThreshold: number;
  nextThreshold: number;
  progressPercent: number;
  crystalsInSegment: number;
  crystalsNeededForNext: number;
  isMaxTier: boolean;
}

/** XP thresholds for Rank/Title progression (1:1 with TITLE_DEFINITIONS order). Exported for Titles tab.
 * Uses an exponential/progressive curve so high ranks take longer to reach. */
export const RANK_XP_THRESHOLDS = [
  0,      // Novice
  100,    // Beginner
  300,    // Explorer
  600,    // Pupil (Apprentice)
  1500,   // Student
  3000,   // Graduate (Scholar)
  5000,   // Connoisseur (Expert)
  8000,   // Literate
  12000,  // Bookworm
  20000,  // Linguist
  30000,  // Translator
  50000,  // Pro
  75000,  // Expert
  100000, // Polyglot
  130000, // Erudite
  170000, // Master
  220000, // Magister
  280000, // Professor
  350000, // Academic
  450000, // Virtuoso
  550000, // Genius
  700000, // Guru
  900000, // AI Master
  1100000, // Thinker
  1400000, // Sage
  1800000, // Seer
  2300000, // Legend
  3000000, // Myth
  4000000, // Immortal
  5000000, // Absolute
];

/**
 * Returns the user's Rank/Title progress based on total XP.
 * Use this for the main Rank display (Новичок, Студент, etc.).
 */
export function getRankProgress(totalXp: number): TitleProgress {
  const thresholds = RANK_XP_THRESHOLDS;
  let currentIndex = 0;
  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (totalXp >= thresholds[i]) {
      currentIndex = Math.min(i, TITLE_DEFINITIONS.length - 1);
      break;
    }
  }

  const current = TITLE_DEFINITIONS[currentIndex];
  const next = currentIndex < TITLE_DEFINITIONS.length - 1 ? TITLE_DEFINITIONS[currentIndex + 1] : null;
  const currentThreshold = thresholds[currentIndex];
  const nextThreshold = next ? thresholds[currentIndex + 1] ?? currentThreshold : currentThreshold;
  const xpInSegment = totalXp - currentThreshold;
  const xpNeededForNext = nextThreshold - currentThreshold;
  const progressPercent = next
    ? Math.min(100, (xpInSegment / xpNeededForNext) * 100)
    : 100;
  const isMaxTier = !next;

  return {
    current,
    next,
    currentThreshold,
    nextThreshold,
    progressPercent,
    crystalsInSegment: xpInSegment,
    crystalsNeededForNext: xpNeededForNext,
    isMaxTier,
  };
}

/**
 * Returns the user's title progress based on total earned crystals.
 * Updates automatically when crystal balance changes.
 * (Kept for backward compatibility; Rank uses getRankProgress(xp) instead.)
 */
export function getTitleProgress(totalCrystals: number): TitleProgress {
  let currentIndex = 0;
  for (let i = TITLE_DEFINITIONS.length - 1; i >= 0; i--) {
    if (totalCrystals >= TITLE_DEFINITIONS[i].threshold) {
      currentIndex = i;
      break;
    }
  }

  const current = TITLE_DEFINITIONS[currentIndex];
  const next = currentIndex < TITLE_DEFINITIONS.length - 1 ? TITLE_DEFINITIONS[currentIndex + 1] : null;
  const currentThreshold = current.threshold;
  const nextThreshold = next?.threshold ?? currentThreshold;
  const crystalsInSegment = totalCrystals - currentThreshold;
  const crystalsNeededForNext = nextThreshold - currentThreshold;
  const progressPercent = next
    ? Math.min(100, (crystalsInSegment / crystalsNeededForNext) * 100)
    : 100;
  const isMaxTier = !next;

  return {
    current,
    next,
    currentThreshold,
    nextThreshold,
    progressPercent,
    crystalsInSegment,
    crystalsNeededForNext,
    isMaxTier,
  };
}
